// Linha de split, card pião (WEB-123). Mesma anatomia do `DeliveryRow` de Webhooks
// (WEB-121, aprovado): a área principal é um `<button>` que abre o drawer, e a ação da
// ponta fica AO LADO dele, nunca dentro (botão dentro de botão é HTML inválido).
//
// Recebidos: `source_seller.display_name`, e da transação do pagador só `id` (copiável) e
// `amount` (`19` §5.2). Nada de cliente, itens, metadata ou e-mail do pagador: a API não
// manda, e o teste unit prova que a linha não inventa.
// Enviados: a visão reduzida não tem o recebedor (`source_seller` é o próprio pagador), então
// a linha identifica a cobrança e aponta para ela ("Questões abertas" de WEB-123).

import { Check, Copy } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import type { Split, SplitRole } from '../../api/queries/splits.ts';
import { formatDate } from '../../lib/dates.ts';
import { formatCents } from '../../lib/money.ts';
import {
  SPLIT_STATUS,
  type SplitTone,
  splitAmountLabel,
  splitKindLabel,
  splitWhen,
  TONE_COLOR,
} from './model.ts';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const FOCUS =
  'focus-visible:ring-2 focus-visible:ring-[color:var(--piao-gold)] focus-visible:outline-none';

export function SplitStatusChip({ status }: { status: Split['status'] }) {
  const { label, tone } = SPLIT_STATUS[status];
  return <ToneChip tone={tone}>{label}</ToneChip>;
}

function ToneChip({ tone, children }: { tone: SplitTone; children: ReactNode }) {
  const color = TONE_COLOR[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 9px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        color,
        background: `color-mix(in oklab, ${color} 12%, transparent)`,
      }}
    >
      {children}
    </span>
  );
}

/** Copia o `transaction.id`: é o que concilia as duas pontas (`19` §5.2). */
export function CopyIdButton({ id, label }: { id: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={copied ? 'Copiado' : label}
      title={copied ? 'Copiado' : label}
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => {
          setCopied(true);
        });
      }}
      className={`piao-ghost ${FOCUS}`}
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 12,
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
      }}
    >
      {copied ? (
        <Check size={16} aria-hidden="true" style={{ color: 'var(--piao-green)' }} />
      ) : (
        <Copy size={16} aria-hidden="true" />
      )}
    </button>
  );
}

export function SplitRow({
  split,
  side,
  divider = false,
  selected = false,
  onSelect,
  transactionLink,
}: {
  split: Split;
  side: SplitRole;
  divider?: boolean;
  selected?: boolean;
  onSelect: () => void;
  /** Só na aba Enviados: o link para `/app/transactions/:id` (o recebedor não tem acesso). */
  transactionLink?: ReactNode;
}) {
  const received = side === 'received';
  const title = received
    ? split.source_seller.display_name
    : `Cobrança de ${formatCents(split.transaction.amount)}`;
  const pending = split.amount === null;

  return (
    <div
      data-testid="split-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        borderTop: divider ? '1px solid var(--piao-surface-border)' : undefined,
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Abrir split ${received ? `de ${title}` : `da ${title.toLowerCase()}`}`}
        data-on={selected}
        className={`piao-row ${FOCUS}`}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 44,
          padding: '12px 8px',
          borderRadius: 12,
          textAlign: 'left',
          fontFamily: 'var(--piao-font)',
          cursor: 'pointer',
        }}
      >
        <span className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ display: 'flex' }}>
          <span style={{ display: 'block', minWidth: 0, flex: '1 1 220px' }}>
            <span
              className="truncate"
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 800,
                color: 'var(--piao-ink)',
              }}
            >
              {title}
            </span>
            <span
              style={{
                display: 'block',
                marginTop: 2,
                fontSize: 12.5,
                color: 'var(--piao-muted)',
              }}
            >
              {splitKindLabel(split)} · {formatDate(splitWhen(split))}
            </span>
            <span
              className="truncate"
              style={{
                display: 'block',
                marginTop: 2,
                fontSize: 12,
                color: 'var(--piao-muted)',
              }}
            >
              {received ? `PIX de ${formatCents(split.transaction.amount)} · ` : ''}
              <span style={{ fontFamily: MONO }}>{split.transaction.id}</span>
            </span>
          </span>
          <span
            className="flex shrink-0 items-center gap-3"
            style={{ display: 'flex', marginLeft: 'auto' }}
          >
            <SplitStatusChip status={split.status} />
            <span
              style={{
                minWidth: 96,
                textAlign: 'right',
                fontSize: pending ? 12.5 : 15,
                fontWeight: pending ? 600 : 800,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                color: pending ? 'var(--piao-muted)' : 'var(--piao-ink)',
              }}
            >
              {splitAmountLabel(split)}
            </span>
          </span>
        </span>
      </button>
      {received ? (
        <CopyIdButton id={split.transaction.id} label="Copiar id da transação" />
      ) : (
        transactionLink
      )}
    </div>
  );
}
